/**
 * Recovery from outdated app files.
 *
 * After a new deployment the file names of the lazy-loaded chunks change and the old files are removed.
 * A device that still runs the old app (cached HTML, old service worker) then asks for a chunk that no
 * longer exists. The server answers with the app page or an error, the browser reports e.g.
 * "'text/html' is not a valid JavaScript MIME type for module script" and the page stays white.
 * Cleaning the service worker and the caches and reloading once fetches the current version.
 */

/** Markers in the messages of the different browsers for a chunk (dynamic import) that could not be loaded. */
const CHUNK_ERROR_PATTERNS: RegExp[] = [
  /is not a valid JavaScript MIME type/i, // Safari, Chrome: server answered with HTML
  /Failed to fetch dynamically imported module/i, // Chrome, Edge
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
  /Loading chunk [\w-]+ failed/i, // webpack style
  /ChunkLoadError/i,
];

/** True when the error means that a lazy-loaded chunk could not be loaded. */
export function isChunkLoadError(error: unknown): boolean {
  const value = error as { message?: unknown; name?: unknown; rejection?: unknown } | string | null | undefined;
  const parts: string[] = [];
  if (typeof value === 'string') {
    parts.push(value);
  } else if (value && typeof value === 'object') {
    if (typeof value.name === 'string') parts.push(value.name);
    if (typeof value.message === 'string') parts.push(value.message);
    if (value.rejection) return isChunkLoadError(value.rejection);
  }
  const text = parts.join(' ');
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text));
}

const RELOAD_FLAG = 'chunk_error_reload_at';
/** After a recovery reload the next one is only done after this time, so a persistent error cannot loop. */
export const RELOAD_GUARD_MS = 60_000;

/** True when a recovery reload is allowed now (none in the last minute). */
export function mayReloadForChunkError(storage: Pick<Storage, 'getItem'> | null, now = Date.now()): boolean {
  try {
    const last = Number(storage?.getItem(RELOAD_FLAG));
    return !(Number.isFinite(last) && last > 0 && now - last < RELOAD_GUARD_MS);
  } catch {
    return true;
  }
}

let recovering = false;

/**
 * Unregisters the service workers, clears the caches and reloads the page once.
 * Returns false when a reload was already done a moment ago (the error then stays visible instead of looping).
 */
export async function recoverFromChunkError(): Promise<boolean> {
  if (recovering || typeof window === 'undefined') {
    return false;
  }
  let storage: Storage | null = null;
  try {
    storage = window.sessionStorage;
  } catch {
    storage = null;
  }
  if (!mayReloadForChunkError(storage)) {
    return false;
  }
  recovering = true;
  try {
    storage?.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    // storage not available: the guard of this page load (recovering) still prevents a second attempt
  }
  try {
    const registrations = (await navigator.serviceWorker?.getRegistrations?.()) ?? [];
    await Promise.all(registrations.map((registration) => registration.unregister()));
    const keys = (await window.caches?.keys?.()) ?? [];
    await Promise.all(keys.map((key) => window.caches.delete(key)));
  } catch {
    // reload anyway
  }
  window.location.reload();
  return true;
}

/** Listens to errors and rejected promises of the page, as a net for chunk errors outside the router. */
export function installChunkErrorRecovery(target: Window = window): void {
  target.addEventListener('error', (event) => {
    if (isChunkLoadError(event.error ?? event.message)) {
      void recoverFromChunkError();
    }
  });
  target.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      void recoverFromChunkError();
    }
  });
}
